import { BarChart3, Check, HelpCircle, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PollInfo } from '../types';

interface PollContentProps {
  poll: PollInfo;
  isOutgoing?: boolean;
  className?: string;
}

/**
 * Компонент для отображения опроса в сообщении
 */
export function PollContent({ poll, isOutgoing, className }: PollContentProps) {
  // Determine if user has voted
  const hasVoted = poll.options.some(o => o.isChosen);

  return (
    <div className={cn('py-1 min-w-[220px]', className)}>
      {/* Poll type indicator */}
      <div
        className={cn(
          'flex items-center gap-1.5 text-xs mb-1',
          isOutgoing ? 'text-white/70' : 'text-muted-foreground'
        )}
      >
        {poll.isQuiz ? (
          <>
            <HelpCircle className="h-3.5 w-3.5" />
            <span>Викторина</span>
          </>
        ) : (
          <>
            <BarChart3 className="h-3.5 w-3.5" />
            <span>{poll.isAnonymous ? 'Анонимный опрос' : 'Опрос'}</span>
          </>
        )}
        {poll.isMultipleChoice && <span>· Несколько ответов</span>}
        {poll.isClosed && <span>· Завершён</span>}
      </div>

      {/* Question */}
      <p className="font-medium mb-2">{poll.question}</p>

      {/* Options */}
      <div className="space-y-2">
        {poll.options.map((option, index) => {
          const percentage = poll.totalVoters > 0
            ? Math.round((option.voters / poll.totalVoters) * 100)
            : 0;

          const isCorrect = poll.isQuiz && poll.correctOptionId === index;
          const isWrong = poll.isQuiz && option.isChosen && !isCorrect;

          return (
            <div key={index} className="relative">
              {/* Progress bar background */}
              <div
                className={cn(
                  'absolute inset-0 rounded transition-all',
                  isCorrect && hasVoted
                    ? 'bg-green-500/20'
                    : isWrong
                      ? 'bg-red-500/20'
                      : isOutgoing
                        ? 'bg-white/10'
                        : 'bg-primary/10'
                )}
                style={{ width: hasVoted ? `${percentage}%` : '0%' }}
              />

              {/* Option content */}
              <div
                className={cn(
                  'relative flex items-center gap-2 px-3 py-1.5 rounded border transition-colors',
                  option.isChosen
                    ? (isOutgoing
                      ? 'border-white/50'
                      : 'border-primary')
                    : 'border-transparent',
                  !hasVoted && !poll.isClosed && 'cursor-pointer hover:bg-muted/50'
                )}
              >
                {/* Checkmark for chosen or correct */}
                {(option.isChosen || (isCorrect && hasVoted)) && (
                  <div
                    className={cn(
                      'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0',
                      isCorrect
                        ? 'bg-green-500 text-white'
                        : isWrong
                          ? 'bg-red-500 text-white'
                          : isOutgoing
                            ? 'bg-white text-primary'
                            : 'bg-primary text-white'
                    )}
                  >
                    <Check className="h-3 w-3" />
                  </div>
                )}

                {/* Option text */}
                <span className="flex-1 text-sm">{option.text}</span>

                {/* Percentage */}
                {hasVoted && (
                  <span
                    className={cn(
                      'text-xs font-medium',
                      isOutgoing ? 'text-white/80' : 'text-muted-foreground'
                    )}
                  >
                    {percentage}%
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Solution hint for quiz */}
      {poll.isQuiz && poll.solution && hasVoted && (
        <div
          className={cn(
            'mt-2 p-2 rounded text-xs',
            isOutgoing ? 'bg-white/10' : 'bg-muted'
          )}
        >
          <p className="font-medium mb-0.5">Пояснение:</p>
          <p className={isOutgoing ? 'text-white/80' : 'text-muted-foreground'}>
            {poll.solution}
          </p>
        </div>
      )}

      {/* Total voters */}
      <div
        className={cn(
          'flex items-center gap-1 mt-2 text-xs',
          isOutgoing ? 'text-white/60' : 'text-muted-foreground'
        )}
      >
        <Users className="h-3 w-3" />
        <span>
          {poll.totalVoters} {getVotersWord(poll.totalVoters)}
        </span>
      </div>
    </div>
  );
}

function getVotersWord(count: number): string {
  const lastTwo = count % 100;
  const lastOne = count % 10;

  if (lastTwo >= 11 && lastTwo <= 19) {
    return 'голосов';
  }

  if (lastOne === 1) {
    return 'голос';
  }

  if (lastOne >= 2 && lastOne <= 4) {
    return 'голоса';
  }

  return 'голосов';
}
